import moment from 'moment-timezone';
import { SCHEDULE_SPECIFIC } from './auctionFormConstants';
import { parseIsoAndTimeToMoment, buildScheduleStartMoment } from './dateUtils';

export const SCHEDULE_ERROR_MESSAGES = {
  required_date: 'This field is required',
  required_time: 'This field is required',
  date_format: 'Date has wrong format',
  time_format: 'Time has wrong format',
  datetime_format: 'Datetime has wrond format',
  start_min_20: 'Auction Start time should be 20 min plus from the current time',
  end_needs_start: 'Set a valid auction start (date and time) before choosing the end.',
};

function hasDashFormat(value) {
  return Boolean(value && String(value).includes('-'));
}

function hasTimeFormat(value) {
  return Boolean(value && String(value).includes(':'));
}

function validateSpecificStart({
  startDateDisplay,
  startDateIso,
  startTime,
  nowMoment = moment(),
}) {
  if (!startDateDisplay || String(startDateDisplay).trim() === '') return 'required_date';
  if (!hasDashFormat(startDateDisplay)) return 'date_format';
  if (!startTime || String(startTime).trim() === '') return 'required_time';
  if (!hasTimeFormat(startTime)) return 'datetime_format';
  const startMoment = parseIsoAndTimeToMoment(startDateIso, startTime);
  if (!startMoment) return 'datetime_format';
  if (startMoment.isBefore(nowMoment.clone().add(20, 'minutes'))) return 'start_min_20';
  return null;
}

function validateSpecificEnd({
  startScheduleValue,
  startDateIso,
  startTime,
  endDateDisplay,
  endDateIso,
  endTime,
  minGapMinutes,
}) {
  if (!endDateDisplay || String(endDateDisplay).trim() === '') return 'required_date';
  if (!hasDashFormat(endDateDisplay)) return 'date_format';
  if (!endTime || String(endTime).trim() === '') return 'required_time';
  if (!hasTimeFormat(endTime)) return 'time_format';
  const endMoment = parseIsoAndTimeToMoment(endDateIso, endTime);
  if (!endMoment) return 'time_format';
  const startMoment = buildScheduleStartMoment(startScheduleValue, startDateIso, startTime);
  if (!startMoment || !startMoment.isValid()) return 'end_needs_start';
  const minEnd = startMoment.clone().add(minGapMinutes, 'minutes');
  if (endMoment.isBefore(minEnd)) return 'end_min_gap';
  return null;
}

export function validateSchedule({
  isEnglishReverseContinuousType,
  reverseContinuousSpecificMinGapMinutes,
  startScheduleValue,
  startDateDisplay,
  startDateIso,
  startTime,
  endScheduleValue,
  endDateDisplay,
  endDateIso,
  endTime,
  nowMoment = moment(),
}) {
  const hasQuickStart = typeof startScheduleValue === 'number' && startScheduleValue > 0;
  const hasSpecificStart = startScheduleValue === SCHEDULE_SPECIFIC;
  let startErrorKey = null;
  let endErrorKey = null;

  if (!hasQuickStart && !hasSpecificStart) {
    startErrorKey = 'required_date';
  } else if (hasSpecificStart) {
    startErrorKey = validateSpecificStart({ startDateDisplay, startDateIso, startTime, nowMoment });
  }

  if (isEnglishReverseContinuousType) {
    const hasQuickEnd =
      typeof endScheduleValue === 'number' &&
      Number.isFinite(endScheduleValue) &&
      endScheduleValue > 0;
    const hasSpecificEnd = endScheduleValue === SCHEDULE_SPECIFIC;
    if (!hasQuickEnd && !hasSpecificEnd) {
      endErrorKey = 'required_date';
    } else if (hasSpecificEnd) {
      endErrorKey = validateSpecificEnd({
        startScheduleValue,
        startDateIso,
        startTime,
        endDateDisplay,
        endDateIso,
        endTime,
        minGapMinutes: reverseContinuousSpecificMinGapMinutes,
      });
    } else if (Number(endScheduleValue) < reverseContinuousSpecificMinGapMinutes) {
      endErrorKey = 'end_min_gap';
    }
  }

  const startMoment = buildScheduleStartMoment(startScheduleValue, startDateIso, startTime);
  const endMoment =
    isEnglishReverseContinuousType && endScheduleValue === SCHEDULE_SPECIFIC
      ? parseIsoAndTimeToMoment(endDateIso, endTime)
      : null;

  return {
    isValid: !startErrorKey && !endErrorKey,
    startErrorKey,
    endErrorKey,
    startMoment,
    endMoment,
  };
}

export function getScheduleErrorMessage(key, { reverseContinuousSpecificMinGapMinutes } = {}) {
  if (key === 'end_min_gap') {
    return `Auction End Time must be at least ${reverseContinuousSpecificMinGapMinutes} minutes after start`;
  }
  return key ? SCHEDULE_ERROR_MESSAGES[key] || null : null;
}
